/* eslint no-shadow: ["error", { "allow": ["state"] }] */

import { get } from "lodash";
import { SOLUTIONS } from "@/helpers/dataLabels";
import {
	COMPARE_NAMES_KEY,
	DATE_FORMAT_KEY,
	MANAGE_LISTS_KEY,
	PKYC_ENTITIES,
	SESSION_ID_KEY,
	SHOULD_PERSIST_KEY,
	SHOW_PKYC,
	SOLUTIONS_KEY,
	TEAM_ID_KEY,
	TENANT_ID_KEY,
	TIME_FRAME_IN_MINUTES_KEY,
	TIMEZONE_KEY,
	USE_BATCH,
	USER_ID_KEY,
	USER_NAME_KEY,
	USER_READ_ONLY_KEY,
	USER_ROLE_KEY,
	USER_TOKEN_KEY,
} from "@/helpers/localStorageLabels";
import { rolesObject } from "@/helpers/roles";
import {
	ERROR_STATUS,
	LOADING_STATUS,
	SUCCESS_STATUS,
} from "@/helpers/statusLabels";
import apiClient from "@/services/apiClient";
import {
	getStoredLoginRealm,
	setStoredLoginRealm,
} from "@/services/authConfig";
import {
	logout as authLogout,
	clearAccessToken,
	getAccessToken,
} from "@/services/authService";
import {
	AUTH_ERROR,
	AUTH_KEYCLOAK_INIT,
	AUTH_LOGOUT,
	AUTH_SUCCESS,
} from "@/store/actions/auth";
import { normalizeTenantId } from "@/utils/tenant";

const state = {
	token: "",
	role: localStorage.getItem(USER_ROLE_KEY) || "",
	readOnlyUser: localStorage.getItem(USER_READ_ONLY_KEY) === "true",
	userName: localStorage.getItem(USER_NAME_KEY) || "",
	userId: localStorage.getItem(USER_ID_KEY) || "",
	tenantId: normalizeTenantId(localStorage.getItem(TENANT_ID_KEY) || ""),
	userProfile: null,
	status: "",
	hasLoadedOnce: false,
	errors: null,
	managerId: "",
	use2FA: false,
	isWire4Eye: true,
	paymentEngine: undefined,
};

const getters = {
	isAuthenticated: (state) => !!state.token,
	authStatus: (state) => state.status,
	hasError: (state) => state.status === ERROR_STATUS,
	userRole: (state) => state.role,
	readOnly: (state) => state.readOnlyUser,
	userName: (state) => state.userName,
	userId: (state) => state.userId,
	tenantId: (state) => state.tenantId,
	managerId: (state) => state.managerId,
	errors: (state) => state.errors,
	use2FA: (state) => state.use2FA,
	manageLists: (state) => state.manageLists,
	tenantMlThreshold: (state) =>
		state.userProfile?.tenantOptions?.global?.mlThreshold || 0.7,
	limitInWorkingHours: (state) =>
		state.userProfile?.tenantOptions?.batch?.limitInWorkingHours || true,
	analystTimeout: (state) => state.userProfile?.tenantOptions?.analystTimeOut,
	isWire4Eye: (state) => state.userProfile?.tenantOptions?.isWire4Eye,
	paymentEngine: (state) => state.paymentEngine,
	// ACH solution mode: SANCTIONS_ONLY | SANCTIONS_AND_VOP | VOP_ONLY.
	// Backend default (and missing-field default) is sanctions only. UI uses
	// this to hide VOP-specific columns/panels when the tenant has not opted
	// into VOP screening — keeps the table relevant to actual work.
	achSolutionMode: (state) =>
		state.userProfile?.tenantOptions?.solutions?.ach?.mode || "SANCTIONS_ONLY",
	achVopEnabled: (state) => {
		const mode = state.userProfile?.tenantOptions?.solutions?.ach?.mode || "";
		return mode === "SANCTIONS_AND_VOP" || mode === "VOP_ONLY";
	},
	achMultiUserAssignment: (state) => {
		const fromProfile =
			state.userProfile?.tenantOptions?.solutions?.ach?.multiUserAssignment;
		if (fromProfile != null) return Boolean(fromProfile);
		return localStorage.getItem("achMultiUserAssignment") === "true";
	},
};

const actions = {
	/**
	 * Initialize auth state from access token + /me API.
	 * Called after SSO callback sets the access token, or after session restore.
	 */
	[AUTH_KEYCLOAK_INIT]: async ({ commit }) => {
		commit(AUTH_KEYCLOAK_INIT);

		try {
			const token = getAccessToken();

			// Fetch full user profile with tenant options from api service
			let userProfile = null;
			try {
				const profileResponse = await apiClient.getCurrentUserProfile();
				userProfile = profileResponse?.data?.resultData;
			} catch (err) {
				console.warn("Failed to fetch user profile:", err);
			}

			// Keycloak can authenticate a subject that has no matching MongoDB
			// user (e.g. provisioned in the IdP but never created locally). If
			// we seat them with a null profile, every downstream call that
			// dereferences userProfile.id fails and the UI freezes — bail so
			// the callback view can log them out and surface a clear message.
			if (!userProfile?.id) {
				const err = new Error("User not recognized");
				err.name = "UserNotRecognizedError";
				throw err;
			}

			const resultData = {
				token,
				id: userProfile?.id || "",
				username: userProfile?.username || userProfile?.email || "",
				role: userProfile?.role || "",
				tenantId: userProfile?.tenantId || "",
				userSolutions: userProfile?.userSolutions || [],
				managerId: userProfile?.managerId || "",
				sessionId: userProfile?.sessionId || "",
				tenantOptions: userProfile?.tenantOptions || null,
				readOnly: userProfile?.readOnly || false,
				pkycEntities: userProfile?.pkycEntities || [],
				teamId: userProfile?.teamId || null,
				manageLists: userProfile?.manageLists || false,
			};

			commit(AUTH_SUCCESS, { resultData });

			// Restore shortAliases for sanction list display names.
			// The old LoginForm.vue fetched these after login; that file was removed
			// in the Keycloak refactor but the lookup in TableWithHits/EntitiesList
			// still depends on localStorage.shortAliases being populated.
			// We store displayName (e.g. "US Consolidated NON-SDN Sanction List (Enhanced Format)")
			// so the UI shows the full human-readable name instead of the raw snake_case sourceId.
			try {
				const summaryRes = await apiClient.getSummaryReport();
				const shortAliases = (
					summaryRes?.data?.resultData[0]?.dataSources || []
				).reduce((acc, item) => {
					if (item.sourceId && item.displayName) {
						acc[item.sourceId] = item.displayName;
					}
					return acc;
				}, {});
				localStorage.setItem("shortAliases", JSON.stringify(shortAliases));
			} catch (err) {
				console.warn("Failed to fetch sanction list aliases:", err);
			}
		} catch (err) {
			commit(AUTH_ERROR, err);
			throw err;
		}
	},

	[AUTH_LOGOUT]: async ({ commit }) => {
		try {
			await authLogout();
		} finally {
			commit(AUTH_LOGOUT);
			// Clear auth cookies
			document.cookie = "refresh_token=; path=/; max-age=0; samesite=lax";
			document.cookie = "auth_realm=; path=/; max-age=0; samesite=lax";
			// Preserve the remembered login realm across the storage wipe so the
			// user skips the email prompt on their next visit.
			const rememberedRealm = getStoredLoginRealm();
			localStorage.clear();
			sessionStorage.clear();
			if (rememberedRealm) {
				setStoredLoginRealm(rememberedRealm.realm, rememberedRealm.email);
			}
			window.location.href = "/login";
		}
	},
};

const mutations = {
	[AUTH_KEYCLOAK_INIT]: (state) => {
		state.status = LOADING_STATUS;
	},
	[AUTH_SUCCESS]: (state, resp) => {
		resp = resp.resultData;
		const tenantId = normalizeTenantId(resp.tenantId);
		const defaultDateFormat = "MM-DD-YYYY HH:mm:ss";
		const defaultTimezone = "America/Chicago";
		const timeFrameInMinutes = 180; //default 3 hours

		if (!resp.tenantOptions) {
			localStorage.setItem(DATE_FORMAT_KEY, defaultDateFormat);
			localStorage.setItem(TENANT_ID_KEY, tenantId);
			localStorage.setItem(TIMEZONE_KEY, defaultTimezone);
			localStorage.setItem(TIME_FRAME_IN_MINUTES_KEY, timeFrameInMinutes);
		} else {
			const { tenantOptions } = resp || {};

			const userSolutions = [];
			for (const solution in tenantOptions.solutions) {
				if (tenantOptions.solutions[solution].active) {
					if (tenantOptions.solutions[solution].name === SOLUTIONS.wire) {
						const is4eye = get(
							tenantOptions.solutions[solution],
							"global.fourEye",
							true,
						);

						state.isWire4Eye = is4eye;
					}
					userSolutions.push(solution);
				}
			}

			if (resp.role === rolesObject.ANALYST) {
				const analystSolutions = [];
				for (const solution of userSolutions) {
					if (resp?.userSolutions?.includes(solution)) {
						analystSolutions.push(solution);
					}
				}
				localStorage.setItem(SOLUTIONS_KEY, analystSolutions);

				// save analyst pkyc solutions to localStorage
				if (userSolutions.includes(SOLUTIONS.pkyc)) {
					const pkycEntities = resp.pkycEntities || [];
					pkycEntities.length &&
						localStorage.setItem(
							PKYC_ENTITIES,
							pkycEntities.join(", ").toLowerCase(),
						);
				}
			} else {
				localStorage.setItem(SOLUTIONS_KEY, userSolutions);
			}

			if (resp.teamId) {
				localStorage.setItem(TEAM_ID_KEY, resp.teamId);
			}

			const tenantTimeframe = {
				wireTimeframe:
					tenantOptions?.solutions[SOLUTIONS.wire]?.global
						?.timeFrameInMinutes || timeFrameInMinutes,
				achTimeframe:
					tenantOptions?.solutions[SOLUTIONS.ach]?.global?.timeFrameInMinutes ||
					timeFrameInMinutes,
				pkycTimeframe:
					tenantOptions?.solutions[SOLUTIONS.pkyc]?.global
						?.timeFrameInMinutes || timeFrameInMinutes,
				tfTimeframe:
					tenantOptions?.solutions[SOLUTIONS.tf]?.global?.timeFrameInMinutes ||
					timeFrameInMinutes,
			};

			state.paymentEngine = tenantOptions.paymentEngine;
			localStorage.setItem(
				DATE_FORMAT_KEY,
				tenantOptions?.dateFormat || defaultDateFormat,
			);
			localStorage.setItem(TENANT_ID_KEY, tenantId || "no-tenant");
			localStorage.setItem(
				TIMEZONE_KEY,
				tenantOptions?.timezone || defaultTimezone,
			);
			localStorage.setItem(
				TIME_FRAME_IN_MINUTES_KEY,
				JSON.stringify(tenantTimeframe),
			);
			localStorage.setItem(
				USE_BATCH,
				tenantOptions?.batch?.isAvailable || false,
			);
			localStorage.setItem(
				COMPARE_NAMES_KEY,
				tenantOptions?.compareNames || false,
			);
			localStorage.setItem(SHOW_PKYC, tenantOptions.showPkyc);
			if (tenantOptions.shouldPersist !== undefined) {
				localStorage.setItem(
					SHOULD_PERSIST_KEY,
					resp.tenantOptions.shouldPersist,
				);
			}
			localStorage.setItem(
				"achMultiUserAssignment",
				tenantOptions?.solutions?.ach?.multiUserAssignment || false,
			);
		}

		localStorage.setItem(SESSION_ID_KEY, resp.sessionId);
		sessionStorage.setItem(USER_TOKEN_KEY, resp.token);
		localStorage.setItem(USER_ROLE_KEY, resp.role);
		localStorage.setItem(USER_READ_ONLY_KEY, resp.readOnly || false);
		localStorage.setItem(USER_NAME_KEY, resp.username);
		localStorage.setItem(USER_ID_KEY, resp.id);

		state.status = SUCCESS_STATUS;
		state.userId = resp.id;
		state.token = resp.token;
		state.readOnlyUser = resp.readOnly || false;
		state.role = resp.role;
		state.userName = resp.username;
		state.userProfile = resp;
		state.hasLoadedOnce = true;
		state.tenantId = tenantId;
		state.use2FA = resp.tenantOptions?.passwordConfig?.use2FA || false;

		// Store manageLists for ADMIN users
		if (resp.role === rolesObject.ADMIN) {
			const manageListsValue = resp.manageLists || false;
			localStorage.setItem(MANAGE_LISTS_KEY, manageListsValue);
			state.manageLists = manageListsValue;
		} else {
			localStorage.removeItem(MANAGE_LISTS_KEY);
			state.manageLists = false;
		}
	},
	[AUTH_ERROR]: (state, err) => {
		state.status = ERROR_STATUS;
		state.errors = err.message;
		state.hasLoadedOnce = true;
	},
	[AUTH_LOGOUT]: (state) => {
		clearAccessToken();
		state.userProfile = null;
		state.token = "";
		state.role = "";
		state.userName = "";
		state.status = "";
		state.tenantId = "";
		state.errors = null;
		state.manageLists = false;
	},
};

export default {
	state,
	getters,
	actions,
	mutations,
};
